import * as React from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, Index, IndexRange, InfiniteLoader, InfiniteLoaderChildProps, List, ListRowProps, Size } from 'react-virtualized';
import BibleBook from 'src/bible/BibleBook';
import BibleChapter from 'src/bible/BibleChapter';
import BibleVerse from 'src/bible/BibleVerse';
import SourceTextCache from 'src/new/sources/SourceTextCache';
import SourceTextCacher from 'src/new/sources/SourceTextCacher';
import VersionStore from 'src/new/versions/VersionStore';
import SearchQuery from 'src/search2/SearchQuery';
import Immutable from 'src/util/Immutable';
import ChapterListItem from './ChapterListItem';
import ForceScroller from './ForceScroller';

interface Props {
    book: BibleBook;
    initialFocusedVerse: Immutable<BibleVerse>;
    chapters: BibleChapter[];
    versions: VersionStore;
    selectedVersionIndex: number;
    sourceTextCache: SourceTextCache;
    highlightQuery: SearchQuery;
    onFocusedVerseChange: (verse: BibleVerse) => void;
    onWordClick: (verseIndex: number, sourceIndex: number, wordIndex: number, partIndex: number, text: string) => void;
}

interface Snapshot {
    topVerse: BibleVerse | null;
}

export default class ChapterList extends React.Component<Props, {}, Snapshot> {

    private focusedVerseNotificationDelayMillis = 100;

    private cellMeasurerCache = new CellMeasurerCache({
        defaultHeight: 500,
        fixedWidth: true
    });

    private firstRenderFocusedVerse: BibleVerse | null;
    private loadedChapterIndexes = new Set<number>();
    private loader = React.createRef<InfiniteLoader>();
    private loaderOnRowsRendered: (params: { startIndex: number; stopIndex: number }) => void;
    private list: List | null = null;
    private listScrollTop: number;
    private forceScroller = new ForceScroller();
    private delayedFocusedVerseNotification: NodeJS.Timer;

    constructor(props: Props) {
        super(props);
        this.firstRenderFocusedVerse = props.initialFocusedVerse.get();
        this.onAutoSizerResize = this.onAutoSizerResize.bind(this);
        this.isRowLoaded = this.isRowLoaded.bind(this);
        this.loadMoreRows = this.loadMoreRows.bind(this);
        this.rowRenderer = this.rowRenderer.bind(this);
        this.onRowsRendered = this.onRowsRendered.bind(this);
        this.onListScroll = this.onListScroll.bind(this);
        this.onScrollOverlayChapterClick = this.onScrollOverlayChapterClick.bind(this);
    }

    public getSnapshotBeforeUpdate(prevProps: Readonly<Props>): Snapshot | null {
        return {
            topVerse: this.getTopVerse(this.props.chapters)
        };
    }

    public render(): React.ReactNode {
        const scrollToIndex = this.firstRenderFocusedVerse !== null ? this.getRowIndexForVerse(this.firstRenderFocusedVerse) : undefined;
        this.firstRenderFocusedVerse = null;

        return (
            <InfiniteLoader
                ref={this.loader}
                isRowLoaded={this.isRowLoaded}
                loadMoreRows={this.loadMoreRows}
                rowCount={this.props.chapters.length}>
                {(loaderProps: InfiniteLoaderChildProps): React.ReactNode => {
                    this.loaderOnRowsRendered = loaderProps.onRowsRendered;
                    return (
                        <AutoSizer
                            onResize={this.onAutoSizerResize}>
                            {(sizerProps: Size): React.ReactNode => (
                                <div style={{ width: sizerProps.width, height: sizerProps.height }}>
                                    <List
                                        //style={{ paddingRight: 50 }} // TEMP: For scroll overlay.
                                        ref={(instance) => this.registerList(instance, loaderProps.registerChild)}
                                        width={sizerProps.width}
                                        height={sizerProps.height}
                                        rowCount={this.props.chapters.length}
                                        estimatedRowSize={this.cellMeasurerCache.defaultHeight}
                                        deferredMeasurementCache={this.cellMeasurerCache}
                                        rowHeight={this.cellMeasurerCache.rowHeight}
                                        rowRenderer={this.rowRenderer}
                                        onRowsRendered={this.onRowsRendered}
                                        scrollToIndex={scrollToIndex}
                                        scrollToAlignment="start"
                                        onScroll={this.onListScroll} />
                                    {/*<VerseListScrollOverlay
                                        book={this.props.book}
                                        height={sizerProps.height}
                                        onChapterClick={this.onScrollOverlayChapterClick} />*/}
                                </div>
                            )}
                        </AutoSizer>
                    );
                }}
            </InfiniteLoader>
        );
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot: Snapshot | undefined): void {
        let reloadRows = false;
        let resizeRows = false;
        let scrollToVerse: BibleVerse | null = null;

        if (prevProps.book.getIndex() !== this.props.book.getIndex()) {
            reloadRows = true;
            resizeRows = true;
        }

        if (prevProps.selectedVersionIndex !== this.props.selectedVersionIndex) {
            resizeRows = true;
            if (snapshot) {
                scrollToVerse = scrollToVerse || snapshot.topVerse;
            }
        }

        if (prevProps.initialFocusedVerse !== this.props.initialFocusedVerse) {
            scrollToVerse = scrollToVerse || this.props.initialFocusedVerse.get();
        }

        if (reloadRows) {
            if (this.loader.current !== null) {
                this.loader.current.resetLoadMoreRowsCache();
            }
            // Force InfiniteLoader to reload rows: https://github.com/bvaughn/react-virtualized/issues/1280
            this.loaderOnRowsRendered({ startIndex: 0, stopIndex: 0 });
        }

        if (resizeRows) {
            this.cellMeasurerCache.clearAll();
            if (this.list !== null) {
                this.list.forceUpdateGrid();
            }
        }

        if (scrollToVerse !== null) {
            this.scrollToVerse(scrollToVerse);
        }
    }

    public componentWillUnmount(): void {
        this.forceScroller.cancelScroll();
    }

    private registerList(list: List | null, registerChild: (registeredChild: any) => void): void {
        this.list = list;
        registerChild(list);
    }

    private getRowIndexForVerse(verse: BibleVerse): number {
        const chapterIndex = verse.getChapter().getIndex();
        return this.props.chapters.findIndex((chapter) => chapter.getIndex() === chapterIndex);
    }

    private scrollToVerse(verse: BibleVerse): void {
        if (this.list !== null) {
            const rowIndex = this.getRowIndexForVerse(verse);
            if (rowIndex > -1) {
                this.forceScroller.scrollToRow(this.list, rowIndex);
            }
        }
    }

    private onAutoSizerResize(info: Size): any {
        const topVerse = this.getTopVerse(this.props.chapters);
        this.cellMeasurerCache.clearAll();
        if (this.list !== null) {
            this.list.recomputeRowHeights();
        }
        if (topVerse !== null) {
            this.scrollToVerse(topVerse);
        }
    }

    private isRowLoaded(params: Index): boolean {
        const chapterIndex = this.props.chapters[params.index].getIndex();
        return this.loadedChapterIndexes.has(chapterIndex);
    }

    private loadMoreRows(params: IndexRange): Promise<any> {
        const startVerseIndex = this.props.chapters[params.startIndex].getVerse(1).getIndex();
        const endVerseIndex = this.props.chapters[params.stopIndex].getVerse(this.props.chapters[params.stopIndex].getVerseCount()).getIndex();
        return SourceTextCacher.cacheVerses(startVerseIndex, endVerseIndex, this.props.sourceTextCache).then(() => {
            for (let chapterIndex = params.startIndex; chapterIndex <= params.stopIndex; chapterIndex++) {
                this.loadedChapterIndexes.add(this.props.chapters[chapterIndex].getIndex());
            }
            return Promise.resolve();
        });
    }

    private rowRenderer(props: ListRowProps): React.ReactNode {
        const chapter = this.props.chapters[props.index];
        let content;

        if (this.loadedChapterIndexes.has(chapter.getIndex())) {
            content = (
                <CellMeasurer
                    cache={this.cellMeasurerCache}
                    rowIndex={props.index}
                    columnIndex={0}
                    parent={props.parent}>
                    <ChapterListItem
                        chapter={chapter}
                        versions={this.props.versions}
                        selectedVersionIndex={this.props.selectedVersionIndex}
                        sourceTextCache={this.props.sourceTextCache}
                        highlightQuery={this.props.highlightQuery}
                        onWordClick={this.props.onWordClick} />
                </CellMeasurer>
            );
        }

        return <div key={props.key} style={props.style}>{content}</div>;
    }

    private onRowsRendered(info: { overscanStartIndex: number; overscanStopIndex: number; startIndex: number; stopIndex: number }): void {
        this.loaderOnRowsRendered(info);
        if (this.forceScroller.isScrolling() && this.list !== null) {
            this.forceScroller.restartScroll(this.list);
        }
    }

    private onListScroll(info: { clientHeight: number; scrollHeight: number; scrollTop: number }): void {
        this.listScrollTop = info.scrollTop;
        this.forceScroller.setListScrollTop(info.scrollTop);

        clearTimeout(this.delayedFocusedVerseNotification);
        this.delayedFocusedVerseNotification = setTimeout(() => {
            const topVerse = this.getTopVerse(this.props.chapters);
            if (topVerse !== null) {
                this.props.onFocusedVerseChange(topVerse);
            }
        }, this.focusedVerseNotificationDelayMillis);
    }

    private onScrollOverlayChapterClick(chapter: BibleChapter): void {
        this.scrollToVerse(chapter.getVerse(1));
    }

    private getTopVerse(chapters: BibleChapter[]): BibleVerse | null {
        if (this.list !== null) {
            for (let rowIndex = 0; rowIndex < chapters.length; rowIndex++) {
                const rowOffset = this.list.getOffsetForRow({ alignment: 'start', index: rowIndex });
                if (rowOffset >= this.listScrollTop) {
                    // If the scroll position is exactly at the top of the row then focus that row
                    // otherwise some of the previous row is visible so focus the previous row.
                    const focusedRowIndex = rowOffset === this.listScrollTop ? rowIndex : Math.max(0, rowIndex - 1);
                    return chapters[focusedRowIndex].getVerse(1);
                }
            }
        }
        return null;
    }
}
